<template>
  <div class="candidate-testing d-flex flex-column fill-height">
    <div class="candidate-testing__header">
      <div style="margin-right: 10px;">
        {{ testPassedText }}
      </div>
      <div>
        <span class="red-text" :class="{ 'green-text': testPassed }">{{ correctQuestionCount }}</span>/{{ testQuestions.length }}
      </div>
    </div>
    <div class="candidate-testing__info">
      <div v-if="testTimer" class="candidate-testing__info_time">
        <span>{{ $t('timeSpent') }}:</span> <span>{{ testTimePassed.min }}:{{ testTimePassed.sec }}</span>
      </div>
      <div class="candidate-testing__info_points">
        <div>
          <span>{{ $t('pointsScored') }}: </span><span>{{ testResultPercent }}% ({{ testResultScore }})</span>
        </div>
        <div>
          <span>{{ $t('passing') }}: </span><span>{{ testMinimalScore }}%</span>
        </div>
      </div>
    </div>
    <VDivider class="my-4" />
    <div class="candidate-testing__questions-block flex-grow-1 huntica-nice-scroll">
      <div v-for="(q, index) in testQuestions" :key="q.order" class="mb-6">
        <CandidateTestResultQuestion
          :question="q"
          :index="index"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  computed, ref, toRefs,
} from '@vue/composition-api';
import { useI18n } from 'vue-i18n-composable';
import { testQuestionTypes, testTypes } from '@/enums';
import CandidateTestResultQuestion from '@/components/Candidate/CandidateTestResultQuestion.vue';
import cloneDeep from 'lodash.clonedeep';

export default {
  name: 'CandidateTestResult',
  components: {
    CandidateTestResultQuestion,
  },
  props: {
    candidate: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const { candidate } = toRefs(props);
    const { t } = useI18n();

    const questionImage = ref(null);

    const testTimer = computed(() => candidate.value?.test.timer);
    const testAnswers = computed(() => candidate.value?.testAnswers);
    const testQuestions = computed(() => cloneDeep(candidate.value?.test.questions)
      .map((q) => {
        const answer = testAnswers.value.find(a => a.testQuestionId === q.id);

        if (!answer) {
          q.candidateAnswer = null;
          return q;
        }

        q.candidateAnswer = answer;
        return q;
      })
      .sort((a, b) => a.order - b.order));

    const totalPoints = computed(() => (testQuestions.value.reduce((sum, q) => sum + Number(q.score), 0)));
    const testResultScore = computed(() => candidate.value?.testScore.score);
    const testResultPercent = computed(() => Math.ceil((testResultScore.value * 100) / totalPoints.value));
    const testMinimalScore = computed(() => candidate.value?.test.minimalScore);

    function isAnswerCorrect(answer, correctAnswer) {
      return answer.answer === correctAnswer.answer;
    }

    function isSelectCorrect(answer, correctAnswer) {
      return answer.answer === correctAnswer.id.toString();
    }

    function isMultiselectCorrect(answer, qAnswers) {
      let correct = true;
      const allCorrectId = qAnswers
        .filter(a => a.isCorrect)
        .map(a => a.id);

      const ans = answer.answer.split(';').map(Number);
      ans.forEach((a) => {
        if (!allCorrectId.includes(a)) {
          correct = false;
        }
      });

      return allCorrectId.length === ans.length && correct;
    }

    const correctQuestionCount = computed(() => {
      let result = 0;

      testQuestions.value.forEach((q) => {
        const answer = q.candidateAnswer;

        if (!answer) {
          return;
        }

        const correctAnswer = q.answers.find(a => a.isCorrect);

        // eslint-disable-next-line default-case
        switch (q.type) {
          case testQuestionTypes.FREE: {
            result += 1;
            break;
          }
          case testQuestionTypes.TEXT: {
            result = isAnswerCorrect(answer, correctAnswer)
              ? result + 1
              : result;
            break;
          }
          case testQuestionTypes.NUMERIC: {
            result = isAnswerCorrect(answer, correctAnswer)
              ? result + 1
              : result;
            break;
          }
          case testQuestionTypes.SELECT: {
            result = isSelectCorrect(answer, correctAnswer)
              ? result + 1
              : result;
            break;
          }
          case testQuestionTypes.MULTISELECT: {
            result = isMultiselectCorrect(answer, q.answers)
              ? result + 1
              : result;
            break;
          }
        }
      });

      return result;
    });

    const testType = computed(() => {
      if (candidate.value?.test.type === testTypes.CUTOFF) {
        return t('cutoff');
      }

      return t('explanation');
    });
    const testPassed = computed(() => candidate.value?.testScore.passed);
    const testPassedText = computed(() => `${testType.value} ${t('test')} ${testPassed.value ? t('passed') : t('notPassed')}`);

    const testTimePassed = computed(() => {
      const totalSeconds = (new Date(candidate.value?.testFinishedAt).getTime() - new Date(candidate.value?.testStartedAt).getTime()) / 1000;
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return {
        min: minutes < 10 ? `0${minutes}` : minutes,
        sec: seconds < 10 ? `0${seconds}` : seconds,
      };
    });

    return {
      testPassed,
      testPassedText,
      testQuestions,
      testAnswers,
      testTimer,
      testTimePassed,
      questionImage,
      testResultScore,
      totalPoints,
      testResultPercent,
      testMinimalScore,
      correctQuestionCount,
      testQuestionTypes,
    };
  },
};
</script>

<style lang="scss" scoped>
.candidate-testing {
  padding: 16px;

  &__header {
    display: flex;
    font-size: 14px;
    line-height: 16.8px;
    font-weight: 400;
    margin-bottom: 5px;
    @media screen and (max-width: 428px) {
      justify-content: space-between;
    }
  }

  &__info {
    color: #929292;
    font-size: 14px;
    line-height: 16.8px;
    display: flex;
    @media screen and (max-width: 1024px) {
      flex-direction: column;
      gap: 3px;
    }

    &_time {
      margin-right: 20px;
      @media screen and (max-width: 599px) {
        margin-right: 0;
      }
      @media screen and (max-width: 428px) {
        display: flex;
        justify-content: space-between;
      }
    }
    &_points {
      display: flex;
      div {
        margin-right: 20px;
        @media screen and (max-width: 428px) {
          display: flex;
          justify-content: space-between;
          margin-right: 0;
        }
      }
      @media screen and (max-width: 428px) {
        flex-direction: column;
      }
    }
  }

  &__question {
    font-size: 14px;
    line-height: 20px;
    &_img-btn {
      display: inline-block;
      cursor: pointer;
      margin-left: 3px;
    }
  }

  &__answer-block {
    margin-top: 7px;
  }

  &__answer {
    font-size: 14px;
    line-height: 20px;
    display: flex;
    align-items: center;
    @media screen and (max-width: 599px) {
      flex-direction: column;
      align-items: flex-start;
    }
    &_no-correct {
      margin-left: 24px;
      color: #929292;
      @media screen and (max-width: 599px) {
        margin-left: 0;
      }
    }
  }

  &__questions-block {
    overflow-y: auto;
    max-height: 100%;
    @media #{map-get($display-breakpoints, 'md-and-up')} {
      height: 0;
    }
  }

  &__dialog-img-content {
    position: relative;
    padding: 50px;
    @media screen and (max-width: 599px) {
      padding: 0;
      position: absolute;
      left: 0;
      top: 50%;
      transform: translate(0, -50%);
      display: flex;
      align-items: center;
      height: 100%;
    }
  }
}
.red-text {
  color: red;
}
.green-text {
  color: #1FC57F;
}

.close-button {
  position: absolute;
  right: 15px;
  top: 15px;
}
.v-image {
  background-color: #fff;
}
</style>

<i18n locale="en" lang="yaml">
  test: test
  passed: passed
  notPassed: not passed
  explanation: Explanation
  cutoff: Сutoff
  noAnswer: There is no answer
  timeSpent: Time elapsed
  correctAnswer: correct answer
  pointsScored: Points earned
  outOf: out of
  passing: Passing
</i18n>

<i18n locale="ru" lang="yaml">
  test: тест
  passed: пройден
  notPassed: не пройден
  explanation: Уточняющий
  cutoff: Отсекающий
  noAnswer: Нет ответа
  timeSpent: Затрачено времени
  correctAnswer: правильный ответ
  pointsScored: Набрано баллов
  outOf: из
  passing: Проходной
</i18n>
